import React from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import Case from 'case';
import { Box } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { AppButton } from '@Components/Control';
import { SingleTextInput } from './ProductFormComponent';
import {
  StyledFormSelect,
  StyledFormSelectMenu,
} from './StyledProductFormComponent';

function formatSimpleOptionLabel(val) {
  if (typeof val === 'boolean') return val ? 'True' : 'False';
  if (val === null || typeof val === 'undefined') return '';
  const s = String(val);
  return Case.title(s.replace(/_/g, ' '));
}

const SelectMenu = ({
  options = null,
  simpleOptions = null,
  overrideOptions = null,
  onClick,
  selectButtonBg,
  highlightText,
  containerBg,
  keepLabel = false,
  round = 'var(--app-radius)',
  ...rest
}) => {
  if (overrideOptions && Array.isArray(overrideOptions)) {
    return (
      <Box round={round} pad="12px" background={containerBg}>
        {overrideOptions.map(({ api, display }) => (
          <StyledFormSelectMenu
            plain
            key={uniqid()}
            label={display}
            onClick={() => onClick(api)}
            hoverIndicator={selectButtonBg}
            highlightText={highlightText}
            {...rest}
          />
        ))}
      </Box>
    );
  }

  if (simpleOptions && !options) {
    return (
      <Box round={round} pad="12px" background={containerBg}>
        {simpleOptions.map((val) => (
          <StyledFormSelectMenu
            plain
            key={uniqid()}
            label={keepLabel ? val : formatSimpleOptionLabel(val)}
            onClick={() => onClick(val)}
            hoverIndicator={selectButtonBg}
            highlightText={highlightText}
            {...rest}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box round={round} pad="12px" background={containerBg}>
      {options.map(({ name }) => (
        <StyledFormSelectMenu
          plain
          key={uniqid()}
          label={name}
          onClick={() => onClick(name)}
          hoverIndicator={selectButtonBg}
          highlightText={highlightText}
          {...rest}
        />
      ))}
    </Box>
  );
};

SelectMenu.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired),
  simpleOptions: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]).isRequired),
  overrideOptions: PropTypes.arrayOf(PropTypes.shape({
    api: PropTypes.string.isRequired,
    display: PropTypes.string.isRequired,
  })),
  onClick: PropTypes.func.isRequired,
  selectButtonBg: PropTypes.string.isRequired,
  highlightText: PropTypes.string.isRequired,
  containerBg: PropTypes.string.isRequired,
  keepLabel: PropTypes.bool,
  round: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

const DropdownSelect = ({
  options = null,
  simpleOptions = null,
  handleFormValues,
  inputConfig = null,
  selectable = null,
  searchable = false,
  autoSelect = false,
  keepLabel = false,
  label = null,
  value = null,
  customPad = null,
  ...rest
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const { theme: { page, button } } = useAppTheme();

  React.useEffect(() => {
    const useAutoSelect = Boolean(inputConfig?.autoSelect === true || autoSelect);
    if ((value === null) && useAutoSelect && simpleOptions && simpleOptions.length > 0) {
      handleFormValues(simpleOptions[0]);
    } else if (value === null && useAutoSelect) {
      handleFormValues(false);
    }
  }, []);

  let displayLabel = label;
  const selectOptions = selectable ? simpleOptions : [false, true];

  let overrideOptions = null;
  if (inputConfig && Array.isArray(inputConfig.overrideOptions)) {
    overrideOptions = inputConfig.overrideOptions;
    if (value && !label) {
      const found = overrideOptions.find(({ api }) => api === value);
      displayLabel = found ? found.display : value;
    }
  } else {
    if (selectable && value && !label && !keepLabel) {
      displayLabel = formatSimpleOptionLabel(value);
    }
    if (selectable && value && !label && keepLabel) displayLabel = value;
    if (!selectable && (value || value === false) && !label) {
      displayLabel = value ? 'True' : 'False';
    }
  }

  let filteredSimpleOptions = selectOptions;
  let filteredOverrideOptions = overrideOptions;

  if (searchable && searchTerm) {
    if (simpleOptions) {
      filteredSimpleOptions = simpleOptions.filter((opt) => (
        opt.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    }

    if (overrideOptions) {
      filteredOverrideOptions = overrideOptions.filter((opt) => (
        opt.display.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    }
  }

  return (
    <StyledFormSelect
      noShadow
      formIcons
      bgColor={page.inputBg}
      borderColor={page.inputBorder}
      highlight={page.focusHighlight}
      onOpen={() => setIsOpen(true)}
      onClose={() => {
        setIsOpen(false);
        setSearchTerm('');
      }}
      isOpen={isOpen}
      customPad={customPad}
      withCloseDropContent={(onClose) => (
        <Box direction="column">
          {searchable && (
            <Box
              height={{ min: 'fit-content' }}
              pad={{ horizontal: '0.5rem', top: '0.5rem' }}
              background={page.inputBg}
              round={{ corner: 'top', size: 'var(--app-radius)' }}
            >
              <SingleTextInput
                focusHighlight={page.focusHighlight}
                value={searchTerm}
                handleFormValues={setSearchTerm}
                placeholder="Type to filter options"
              />
            </Box>
          )}
          <SelectMenu
            selectButtonBg={button.hoverBg}
            highlightText={button.hoverText}
            containerBg={page.inputBg}
            round={searchable ? { corner: 'bottom', size: 'var(--app-radius)' } : 'var(--app-radius)'}
            options={options}
            simpleOptions={filteredSimpleOptions}
            overrideOptions={filteredOverrideOptions}
            keepLabel={keepLabel}
            onClick={(val) => {
              handleFormValues(val);
              setSearchTerm('');
              onClose();
            }}
            {...rest}
          />
        </Box>
      )}
      {...rest}
    >
      {(Icon) => (
        <AppButton
          fill
          dropDown
          textSize="1rem"
          color={page.inputText}
          label={displayLabel || 'Select an option'}
          icon={<Icon color={page.inputIcon} size="1.25rem" />}
          {...rest}
        />
      )}
    </StyledFormSelect>
  );
};

DropdownSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
  }).isRequired),
  simpleOptions: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]).isRequired),
  inputConfig: PropTypes.shape({
    overrideOptions: PropTypes.arrayOf(PropTypes.shape({
      api: PropTypes.string.isRequired,
      display: PropTypes.string.isRequired,
    })),
    autoSelect: PropTypes.bool,
  }),
  handleFormValues: PropTypes.func.isRequired,
  selectable: PropTypes.bool,
  searchable: PropTypes.bool,
  autoSelect: PropTypes.bool,
  keepLabel: PropTypes.bool,
  label: PropTypes.string,
  customPad: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
};

export default DropdownSelect;
